import React, { useEffect, useState, useCallback } from 'react';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import * as cashflowService from '../../services/cashflowService';
import CashFlowTabs from '../../components/CashFlowTabs';
import ScrollX from '../../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Accounts' three tables - role-gated per cashflow_routes.py's
// ACCOUNTS_ROLES, unlike the rest of the module (Purchase/Site/Projects
// logging is open to anyone logged in, matching expense_routes.py's
// convention) - these were described as owned by a specific function.
// HOD is unscoped (any department) - Purchase/Projects/Site HODs need this
// too, not only a dedicated Accounts_Manager account.
const ACCOUNTS_ROLES = ['HOD', 'Accounts_Manager', 'System_Admin', 'CEO'];

const initialCashForm = { amount: '', as_of_date: '', notes: '' };
const initialRecurringForm = { category: '', amount: '', frequency: 'monthly', next_due_date: '', notes: '' };
const initialPdcForm = { payee: '', purpose: '', item_type: 'pdc', amount: '', due_date: '', cheque_number: '' };

const AccountsPage = () => {
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const canManage = user && ACCOUNTS_ROLES.includes(user.role);

  const [latestCash, setLatestCash] = useState(null);
  const [cashHistory, setCashHistory] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [pdcs, setPdcs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showCashForm, setShowCashForm] = useState(false);
  const [cashForm, setCashForm] = useState(initialCashForm);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [recurringForm, setRecurringForm] = useState(initialRecurringForm);
  const [showPdcForm, setShowPdcForm] = useState(false);
  const [pdcForm, setPdcForm] = useState(initialPdcForm);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [latestRes, historyRes, recurringRes, pdcRes] = await Promise.all([
        cashflowService.getLatestCashPosition(),
        cashflowService.getCashPositionHistory(),
        cashflowService.getRecurringExpenses({ is_active: true }),
        cashflowService.getPostDatedItems(),
      ]);
      setLatestCash(latestRes.data.cash_position);
      setCashHistory(historyRes.data.cash_positions || []);
      setRecurring(recurringRes.data.recurring_expenses || []);
      setPdcs(pdcRes.data.post_dated_items || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load Accounts data', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCashSubmit = async (e) => {
    e.preventDefault();
    if (!cashForm.amount) { showToast('Amount is required', 'error'); return; }
    try {
      await cashflowService.createCashPosition({
        amount: Number(cashForm.amount), as_of_date: cashForm.as_of_date || null, notes: cashForm.notes || null,
      });
      showToast('Cash position recorded', 'success');
      setCashForm(initialCashForm);
      setShowCashForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to record cash position', 'error');
    }
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    if (!recurringForm.category || !recurringForm.amount || !recurringForm.next_due_date) {
      showToast('Category, amount, and next due date are required', 'error');
      return;
    }
    try {
      await cashflowService.createRecurringExpense({
        category: recurringForm.category, amount: Number(recurringForm.amount), frequency: recurringForm.frequency,
        next_due_date: recurringForm.next_due_date, notes: recurringForm.notes || null,
      });
      showToast('Recurring expense added', 'success');
      setRecurringForm(initialRecurringForm);
      setShowRecurringForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to add recurring expense', 'error');
    }
  };

  const handleDeactivateRecurring = async (r) => {
    if (!window.confirm(`Deactivate "${r.category}"? It will stop appearing in the forecast.`)) return;
    try {
      await cashflowService.deleteRecurringExpense(r.id);
      showToast('Deactivated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to deactivate', 'error');
    }
  };

  const handlePdcSubmit = async (e) => {
    e.preventDefault();
    if (!pdcForm.payee || !pdcForm.amount || !pdcForm.due_date) {
      showToast('Payee, amount, and due date are required', 'error');
      return;
    }
    try {
      await cashflowService.createPostDatedItem({
        payee: pdcForm.payee, purpose: pdcForm.purpose || null, item_type: pdcForm.item_type,
        amount: Number(pdcForm.amount), due_date: pdcForm.due_date, cheque_number: pdcForm.cheque_number || null,
      });
      showToast('Item added', 'success');
      setPdcForm(initialPdcForm);
      setShowPdcForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to add item', 'error');
    }
  };

  const handleMarkCleared = async (p) => {
    try {
      await cashflowService.markPostDatedItemCleared(p.id);
      showToast('Marked cleared', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update', 'error');
    }
  };

  const handleDeletePdc = async (p) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await cashflowService.deletePostDatedItem(p.id);
      showToast('Deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete', 'error');
    }
  };

  if (isLoading && !latestCash && recurring.length === 0) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-6xl mx-auto">
          <CashFlowTabs />
          <p className="text-white/80 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <CashFlowTabs />
        <h1 className="text-2xl font-bold text-white mb-6">Accounts</h1>
        {!canManage && (
          <p className="text-white/70 text-xs mb-4">Read-only - only Accounts, System Admin, or CEO can add or edit these entries.</p>
        )}

        {/* Cash position */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">Cash position</h2>
            {canManage && (
              <button onClick={() => setShowCashForm((v) => !v)} className="text-xs px-3 py-1.5 rounded bg-komal-blue text-white font-semibold">
                {showCashForm ? 'Cancel' : '+ Record Snapshot'}
              </button>
            )}
          </div>
          {latestCash ? (
            <p className="text-sm text-gray-700 mb-3">
              Latest: <span className="font-bold text-lg">{money(latestCash.amount)}</span> as of {latestCash.as_of_date}
              {latestCash.notes && <span className="text-gray-400"> - {latestCash.notes}</span>}
            </p>
          ) : (
            <p className="text-sm text-gray-400 mb-3">No cash position recorded yet - the forecast will assume ₹0 opening balance.</p>
          )}
          {showCashForm && (
            <form onSubmit={handleCashSubmit} className="grid md:grid-cols-3 gap-3 mb-3 bg-gray-50 rounded p-3">
              <input type="number" placeholder="Amount (₹) *" value={cashForm.amount} onChange={(e) => setCashForm({ ...cashForm, amount: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="date" value={cashForm.as_of_date} onChange={(e) => setCashForm({ ...cashForm, as_of_date: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" placeholder="As of (defaults to today)" />
              <input placeholder="Notes" value={cashForm.notes} onChange={(e) => setCashForm({ ...cashForm, notes: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <button type="submit" className="text-sm px-4 py-1.5 rounded bg-komal-blue text-white font-semibold w-fit">Save</button>
            </form>
          )}
          {cashHistory.length > 0 && (
            <ScrollX>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-3 py-1.5 font-medium">Date</th>
                    <th className="px-3 py-1.5 font-medium">Amount</th>
                    <th className="px-3 py-1.5 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {cashHistory.map((c) => (
                    <tr key={c.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-1.5">{c.as_of_date}</td>
                      <td className="px-3 py-1.5 font-medium">{money(c.amount)}</td>
                      <td className="px-3 py-1.5 text-gray-400">{c.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>

        {/* Recurring expenses */}
        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recurring expenses ({recurring.length})</h2>
            {canManage && (
              <button onClick={() => setShowRecurringForm((v) => !v)} className="text-xs px-3 py-1.5 rounded bg-komal-blue text-white font-semibold">
                {showRecurringForm ? 'Cancel' : '+ Add'}
              </button>
            )}
          </div>
          {showRecurringForm && (
            <form onSubmit={handleRecurringSubmit} className="grid md:grid-cols-4 gap-3 p-4 bg-gray-50">
              <input placeholder="Category (e.g. Salaries & wages) *" value={recurringForm.category} onChange={(e) => setRecurringForm({ ...recurringForm, category: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="number" placeholder="Amount (₹) *" value={recurringForm.amount} onChange={(e) => setRecurringForm({ ...recurringForm, amount: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <select value={recurringForm.frequency} onChange={(e) => setRecurringForm({ ...recurringForm, frequency: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="one_time">One-time</option>
              </select>
              <input type="date" value={recurringForm.next_due_date} onChange={(e) => setRecurringForm({ ...recurringForm, next_due_date: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <button type="submit" className="text-sm px-4 py-1.5 rounded bg-komal-blue text-white font-semibold w-fit">Save</button>
            </form>
          )}
          {recurring.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No recurring expenses recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Category</th>
                    <th className="px-4 py-2 font-medium">Frequency</th>
                    <th className="px-4 py-2 font-medium">Next due</th>
                    <th className="px-4 py-2 font-medium">Amount</th>
                    {canManage && <th className="px-4 py-2 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {recurring.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">{r.category}</td>
                      <td className="px-4 py-3 text-gray-500">{r.frequency.replace('_', '-')}</td>
                      <td className="px-4 py-3">{r.next_due_date}</td>
                      <td className="px-4 py-3 font-medium">{money(r.amount)}</td>
                      {canManage && (
                        <td className="px-4 py-3">
                          <button onClick={() => handleDeactivateRecurring(r)} className="text-red-500 hover:underline text-xs font-semibold">Deactivate</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>

        {/* Post-dated cheques & dues */}
        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Post-dated cheques & payment dues ({pdcs.length})</h2>
            {canManage && (
              <button onClick={() => setShowPdcForm((v) => !v)} className="text-xs px-3 py-1.5 rounded bg-komal-blue text-white font-semibold">
                {showPdcForm ? 'Cancel' : '+ Add'}
              </button>
            )}
          </div>
          {showPdcForm && (
            <form onSubmit={handlePdcSubmit} className="grid md:grid-cols-3 gap-3 p-4 bg-gray-50">
              <input placeholder="Payee *" value={pdcForm.payee} onChange={(e) => setPdcForm({ ...pdcForm, payee: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <select value={pdcForm.item_type} onChange={(e) => setPdcForm({ ...pdcForm, item_type: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm">
                <option value="pdc">Post-dated cheque</option>
                <option value="statutory_due">Statutory due</option>
                <option value="other">Other</option>
              </select>
              <input type="number" placeholder="Amount (₹) *" value={pdcForm.amount} onChange={(e) => setPdcForm({ ...pdcForm, amount: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="date" value={pdcForm.due_date} onChange={(e) => setPdcForm({ ...pdcForm, due_date: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input placeholder="Cheque number" value={pdcForm.cheque_number} onChange={(e) => setPdcForm({ ...pdcForm, cheque_number: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input placeholder="Purpose" value={pdcForm.purpose} onChange={(e) => setPdcForm({ ...pdcForm, purpose: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <button type="submit" className="text-sm px-4 py-1.5 rounded bg-komal-blue text-white font-semibold w-fit">Save</button>
            </form>
          )}
          {pdcs.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No post-dated cheques or dues recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Payee / purpose</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Due</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Amount</th>
                    {canManage && <th className="px-4 py-2 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {pdcs.map((p) => (
                    <tr key={p.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">
                        {p.payee}
                        {p.purpose && <div className="text-xs text-gray-400">{p.purpose}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded font-semibold bg-blue-100 text-blue-800">{p.item_type.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3">{p.due_date}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-semibold ${p.status === 'cleared' ? 'bg-green-100 text-green-800' : p.status === 'bounced' ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-800'}`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3 font-medium">{money(p.amount)}</td>
                      {canManage && (
                        <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                          {p.status === 'pending' && (
                            <button onClick={() => handleMarkCleared(p)} className="text-green-700 hover:underline text-xs font-semibold">Mark Cleared</button>
                          )}
                          <button onClick={() => handleDeletePdc(p)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountsPage;
